import type { ChatSessionStatus } from "@qw/graphql";

import { resolveAssistantProgressLabel } from "../../assistant-progress-state";
import type { AiSdkChatStatus } from "../../hooks/action-for-stream-state-event";
import type { ElicitationSubmitPhase } from "../../hooks/elicitation-submit-phase";

/**
 * Composer placeholder copy, as a pure function of the form/session state so it
 * can be unit-tested without rendering the component (see the colocated test).
 *
 * Progress copy delegates to the shared assistant-progress resolver, so a `RESUMING`
 * session shows the recovery-specific "Interrupted — attempting to resume…" copy
 * across both the transcript and composer instead of the generic "Alby is thinking…".
 * The composer keeps its local read-only/recording/transcription precedence, then
 * delegates all chat-work precedence and copy to that shared policy.
 */
export const getChatFormPlaceholder = (params: {
  hasChatSession: boolean;
  isReadOnly: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  status: AiSdkChatStatus;
  elicitationSubmitPhase: ElicitationSubmitPhase;
  sessionStatus: ChatSessionStatus | null | undefined;
}): string => {
  const { hasChatSession, isReadOnly, isRecording, isProcessing, status, elicitationSubmitPhase, sessionStatus } =
    params;

  if (hasChatSession && isReadOnly) {
    return "You can't send messages to this chat";
  }

  if (isRecording) {
    return "Recording... (click stop when done)";
  }

  if (isProcessing) {
    return "Transcribing audio...";
  }

  const progressLabel = resolveAssistantProgressLabel({
    status,
    elicitationSubmitPhase,
    sessionStatus,
  });
  if (progressLabel) {
    return progressLabel;
  }

  return "Ask anything, '/' to quickly run a playbook";
};
